import dnd from '../assets/Department_of_National_Defence_(Canada).svg.png';
import { persistentAtom, persistentBoolean } from '@nanostores/persistent';
import z from 'zod';

export const metadataSchema = z.object({
  surname: z.string(),
  rank: z.string(),
  serial: z.string(),
  unit: z.string(),
  location: z.string(),
  telephone: z.string(),
  extra: z.string(),
});
export type Metadata = z.infer<typeof metadataSchema>;

const encoder = {
  encode: JSON.stringify,
  decode: JSON.parse,
};

export const $metadata = persistentAtom<Metadata>(
  'metadata',
  {
    surname: '',
    rank: '',
    serial: '',
    unit: '',
    location: '',
    telephone: '',
    extra: '',
  },
  encoder,
);

export const itemSchema = z.object({
  stockNumber: z.string(),
  description: z.string(),
  serials: z.array(z.string()).optional(), // TODO: figure out if I want to actually use this after all
  quantities: z.array(z.number().or(z.null())).length(10),
});
export type Item = z.infer<typeof itemSchema>;

export const $items = persistentAtom<Item[]>('items', [], encoder);

export function defaultItem(): Item {
  return {
    stockNumber: '',
    description: '',
    quantities: new Array(10).fill(undefined),
  };
}

export const $image = persistentAtom<string>('image', dnd, encoder);
export const $imageHeight = persistentAtom<number>('imageHeight', 40, encoder);

export const $showPageNumbers = persistentBoolean('showPageNumbers', true);

export const serializeDataSchema = z.object({
  metadata: metadataSchema,
  items: z.array(itemSchema),
  image: z.string(),
  imageHeight: z.number(),
  showPageNumbers: z.boolean(),
});
export type SerializeData = z.infer<typeof serializeDataSchema>;

export function serializeData(): SerializeData {
  return {
    metadata: $metadata.get(),
    items: $items.get(),
    image: $image.get() || dnd,
    imageHeight: $imageHeight.get(),
    showPageNumbers: $showPageNumbers.get(),
  };
}

export function loadDeserializedData(data: SerializeData) {
  $metadata.set(data.metadata);
  $items.set(data.items);
  $image.set(data.image || dnd);
  $imageHeight.set(data.imageHeight);
  $showPageNumbers.set(data.showPageNumbers);
}

export function deserializeData(data: any): SerializeData {
  const result = serializeDataSchema.safeParse(data);
  if (result.success) {
    return result.data;
  } else {
    throw result.error;
  }
}

export function loadData(data: any) {
  loadDeserializedData(deserializeData(data));
}

export function resetData() {
  $metadata.set({
    surname: '',
    rank: '',
    serial: '',
    unit: '',
    location: '',
    telephone: '',
    extra: '',
  });
  $items.set([]);
}
